// Figma: "CRM / Resident Detail / Communication Tab / Default" (18428:69917) — Summary
// (3 stat cards) + History (a grouped, reverse-chronological timeline). Renders for the
// "Communications" tab in ./ResidentDetailDrawer.js.
//
// LOGIC PORTED from LC2's CommunicationHistory (src/pages/Resident/ResidentPageInfo.js):
// same three lazy queries (useLazyGetEmailHistoryQuery/TextHistoryQuery/NotificationHistoryQuery,
// see ../../../features/messagingApi/apiSlice.js — a separate "messaging service" host/auth,
// ported alongside this), triggered off `resident.mobileUserId` (mobile_user_id__c), and the
// same summary-stat math (../../../commons/utils/messagingUtils.js). Two differences from LC2:
//
// 1. Sort order: LC2 sorted its combined timeline ascending (oldest first). Figma's own
//    grouping ("This week" above "August 2026" above "August 2024") only makes sense newest
//    first, so this sorts descending instead — matches the design, not LC2's rendering order.
// 2. Push notification status copy: Figma consistently relabels push's `delivered` flag as
//    "Opened" everywhere (summary card says "Open rate", history rows and the preview modal
//    say "Opened") even though LC2's own field is `delivered`, not `opened` (push has no
//    separate "opened" concept — SMS is the same, only `delivered`). This reads `ele.delivered`
//    under the hood but labels it "Opened"/"Open rate" to match the design.
//
// Fields beyond what LC2's CommunicationHistory/HistoryDataElement actually reads
// (opened/clicked/delivered/body/message/js_datestamp/timestamp) aren't confirmed against a
// live response — subject/title/to/bounced-style fields the Figma preview modals show have no
// prior caller in this codebase, so they're read defensively (several plausible key names,
// graceful fallback) rather than assumed.
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, CircularProgress, Stack, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import {useLazyGetEmailHistoryQuery, useLazyGetNotificationHistoryQuery, useLazyGetTextHistoryQuery} from '../../../features/messagingApi/apiSlice';
import {findEmailSummaryStats, findPushSummaryStats, findTextSummaryStats} from '../../../commons/utils/messagingUtils';
import CommunicationPreviewModal from './CommunicationPreviewModal';

import emailSummaryIcon from '../../../assets/communications/email-summary-icon.svg';
import pushSummaryIcon from '../../../assets/communications/push-summary-icon.svg';
import smsSummaryIcon from '../../../assets/communications/sms-summary-icon.svg';
import emailRowIcon from '../../../assets/communications/email-row-icon.svg';
import pushRowIcon from '../../../assets/communications/push-row-icon.svg';
import smsRowIcon from '../../../assets/communications/sms-row-icon.svg';

const ROW_ICON = {email: emailRowIcon, push: pushRowIcon, sms: smsRowIcon};

const getItemDate = (item) => {
    const raw = item.js_datestamp || item.timestamp || item.date || item.created_at;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Whichever "outcome" state a message reached, independent of how it's worded per surface
// (see header comment #2 for why push's `delivered` reads as "Opened").
const getStatusOutcome = (item, type) => {
    const failed = item.bounced || item.failed || item.status === 'failed' || item.status === 'bounced';
    if (failed) return {outcome: type === 'sms' ? 'Not delivered' : 'Bounced', tone: 'error', failed: true};

    if (type === 'sms') {
        return item.delivered ? {outcome: 'Delivered', tone: 'success'} : {outcome: 'Not delivered', tone: 'neutral'};
    }

    const openedFlag = type === 'push' ? item.delivered : item.opened;
    if (item.clicked) return {outcome: 'Opened · Clicked', tone: 'success'};
    if (openedFlag) return {outcome: 'Opened', tone: 'success'};
    return {outcome: type === 'push' ? 'Not tapped' : 'Unopened', tone: 'neutral'};
};

const TONE_COLOR = {success: tokens.color.success500, error: tokens.color.error500, neutral: tokens.color.neutral600};

const getRowStatusLabel = (item, type) => {
    const {outcome, failed} = getStatusOutcome(item, type);
    if (failed) return `Failed · ${outcome}`;
    if (type === 'sms') return outcome;
    return `Sent · ${outcome}`;
};

const truncate = (text, max) => {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const getRowTitle = (item, type) => {
    if (type === 'email') return item.subject || item.title || truncate(item.body || item.message, 48) || 'Email sent';
    if (type === 'push') return item.title || item.subject || truncate(item.body || item.message, 48) || 'Push notification';
    return item.subject || item.title || truncate(item.body || item.message, 48) || 'Text message';
};

const TYPE_LABEL = {email: 'Email', push: 'Push', sms: 'SMS'};

const formatRowDate = (date) => {
    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 60) return `${Math.max(diffMinutes, 0)} minute${diffMinutes === 1 ? '' : 's'} ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
};

const startOfWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    return start;
};

const groupByPeriod = (items) => {
    const weekStart = startOfWeek();
    const groups = new Map();

    items.forEach((item) => {
        const key = item.date >= weekStart ? 'This week' : item.date.toLocaleDateString(undefined, {month: 'long', year: 'numeric'});
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
    });

    return Array.from(groups.entries()).map(([title, rows]) => ({title, rows}));
};

const SUMMARY_CARDS = [
    {key: 'email', label: 'Email', icon: emailSummaryIcon, rows: [{label: 'Open rate', stat: 'openRate'}, {label: 'Click rate', stat: 'clickRate'}]},
    {
        key: 'push',
        label: 'Push notification',
        icon: pushSummaryIcon,
        rows: [
            {label: 'Open rate', stat: 'deliveryRate'},
            {label: 'Click rate', stat: 'clickRate'}
        ]
    },
    {key: 'sms', label: 'SMS', icon: smsSummaryIcon, rows: [{label: 'Delivery rate', stat: 'deliveryRate'}, {label: 'Click rate', stat: 'clickRate'}]}
];

const SummaryCard = ({label, icon, rows, stats}) => (
    <Box sx={{flex: 1, minWidth: 0, border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.control, overflow: 'hidden'}}>
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{p: 1, borderBottom: `1px solid ${tokens.color.neutral200}`}}
        >
            <Typography variant="caption" sx={{color: tokens.color.neutral600}}>
                {label}
            </Typography>
            <Box component="img" src={icon} alt="" sx={{width: 16, height: 16}} />
        </Stack>
        <Stack sx={{p: 1}} spacing={0.5}>
            {rows.map(({label: rowLabel, stat}) => (
                <Stack key={rowLabel} direction="row" justifyContent="space-between">
                    <Typography variant="caption" sx={{color: tokens.color.neutral600}}>
                        {rowLabel}
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                        {stats && Number.isFinite(stats[stat]) ? `${stats[stat]}%` : '-'}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    </Box>
);

SummaryCard.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    rows: PropTypes.array.isRequired,
    stats: PropTypes.object
};

const HistoryRow = ({item, isLast, onOpen}) => (
    <Stack direction="row" spacing={0.75} sx={{position: 'relative'}}>
        <Stack alignItems="center" sx={{flexShrink: 0, width: '32px'}}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '999px',
                    border: `1px solid ${tokens.color.neutral200}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}
            >
                <Box component="img" src={ROW_ICON[item.type]} alt="" sx={{width: 16, height: 16}} />
            </Box>
            {!isLast && <Box sx={{width: '1px', flex: 1, backgroundColor: tokens.color.neutral200, mt: 0.5}} />}
        </Stack>
        <Stack sx={{flex: 1, minWidth: 0, pb: 2}} spacing={0.25}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{color: tokens.color.neutral950}}>
                    {`${TYPE_LABEL[item.type]}: `}
                    <ButtonBase
                        onClick={() => onOpen(item)}
                        sx={{color: tokens.color.sky600, textDecoration: 'underline', fontSize: 'inherit', verticalAlign: 'baseline'}}
                    >
                        {item.rowTitle}
                    </ButtonBase>
                </Typography>
                <Typography variant="caption" sx={{color: tokens.color.neutral500, flexShrink: 0, whiteSpace: 'nowrap'}}>
                    {formatRowDate(item.date)}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{color: TONE_COLOR[item.tone]}}>
                {item.statusLabel}
            </Typography>
        </Stack>
    </Stack>
);

HistoryRow.propTypes = {item: PropTypes.object.isRequired, isLast: PropTypes.bool, onOpen: PropTypes.func.isRequired};

const CommunicationsPanel = ({resident}) => {
    const mobileUserId = resident?.mobileUserId;
    const [activeItem, setActiveItem] = useState(null);

    const [fetchEmailHistory, {data: emails = [], isLoading: isEmailLoading, isFetching: isEmailFetching}] = useLazyGetEmailHistoryQuery();
    const [fetchTextHistory, {data: texts = [], isLoading: isTextLoading, isFetching: isTextFetching}] = useLazyGetTextHistoryQuery();
    const [fetchNotificationHistory, {data: notifications = [], isLoading: isPushLoading, isFetching: isPushFetching}] =
        useLazyGetNotificationHistoryQuery();

    useEffect(() => {
        if (mobileUserId) {
            fetchEmailHistory(mobileUserId);
            fetchTextHistory(mobileUserId);
            fetchNotificationHistory(mobileUserId);
        }
    }, [mobileUserId, fetchEmailHistory, fetchTextHistory, fetchNotificationHistory]);

    const isLoading = isEmailLoading || isEmailFetching || isTextLoading || isTextFetching || isPushLoading || isPushFetching;

    const emailArr = Array.isArray(emails) ? emails : [];
    const textArr = Array.isArray(texts) ? texts : [];
    const pushArr = Array.isArray(notifications) ? notifications : [];

    const stats = useMemo(
        () => ({
            email: emailArr.length ? findEmailSummaryStats(emailArr) : null,
            push: pushArr.length ? findPushSummaryStats(pushArr) : null,
            sms: textArr.length ? findTextSummaryStats(textArr) : null
        }),
        [emailArr, pushArr, textArr]
    );

    const timeline = useMemo(() => {
        const normalize = (list, type) =>
            list
                .map((raw) => {
                    const date = getItemDate(raw);
                    if (!date) return null;
                    return {
                        type,
                        date,
                        rowTitle: getRowTitle(raw, type),
                        statusLabel: getRowStatusLabel(raw, type),
                        tone: getStatusOutcome(raw, type).tone,
                        modal: {
                            sentLabel: `Sent on ${date.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})} at ${date.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'})}`,
                            statusLabel: getStatusOutcome(raw, type).outcome,
                            statusColor: TONE_COLOR[getStatusOutcome(raw, type).tone],
                            subject: raw.subject || raw.title,
                            title: raw.title || raw.subject,
                            to: raw.to || raw.phone || resident?.phone,
                            body: raw.body || raw.message
                        }
                    };
                })
                .filter(Boolean);

        const all = [...normalize(emailArr, 'email'), ...normalize(pushArr, 'push'), ...normalize(textArr, 'sms')];
        all.sort((a, b) => b.date - a.date);
        return all;
    }, [emailArr, pushArr, textArr, resident?.phone]);

    const groups = useMemo(() => groupByPeriod(timeline), [timeline]);

    if (!mobileUserId) {
        return (
            <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                No communication record available for this resident.
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Stack alignItems="center" sx={{py: 4}}>
                <CircularProgress size={20} thickness={5} sx={{color: tokens.color.neutral500}} />
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Stack spacing={1}>
                <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                    Summary
                </Typography>
                <Stack direction="row" spacing={1.5}>
                    {SUMMARY_CARDS.map((card) => (
                        <SummaryCard key={card.key} label={card.label} icon={card.icon} rows={card.rows} stats={stats[card.key]} />
                    ))}
                </Stack>
            </Stack>

            <Stack spacing={1}>
                <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                    History
                </Typography>
                {groups.length === 0 ? (
                    <Typography variant="body2" sx={{color: tokens.color.neutral500, py: 4, textAlign: 'center'}}>
                        No communication history yet.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {groups.map((group) => (
                            <Stack key={group.title} spacing={1}>
                                <Typography variant="body2" fontWeight={600} sx={{color: tokens.color.neutral950}}>
                                    {group.title}
                                </Typography>
                                <Stack>
                                    {group.rows.map((item, index) => (
                                        <HistoryRow
                                            key={`${item.type}-${item.date.getTime()}-${index}`}
                                            item={item}
                                            isLast={index === group.rows.length - 1}
                                            onOpen={setActiveItem}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>

            <CommunicationPreviewModal
                open={Boolean(activeItem)}
                onClose={() => setActiveItem(null)}
                type={activeItem?.type}
                item={activeItem?.modal}
            />
        </Stack>
    );
};

CommunicationsPanel.propTypes = {
    resident: PropTypes.object
};

export default CommunicationsPanel;
